import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Marketplaces com comissao oficial sincronizada (ver Edge Function
 *  sync-commissions). Os demais marketplaces nao tem dado oficial disponivel. */
export type OfficialCommissionMarketplace = "shopee" | "awin";

export interface Commission {
  id: string;
  tenant_id: string;
  marketplace_code: OfficialCommissionMarketplace;
  external_transaction_id: string;
  order_id: string | null;
  status: "pending" | "approved" | "declined" | "paid";
  product_name: string | null;
  sale_amount_cents: number | null;
  commission_amount_cents: number;
  currency: string;
  transaction_date: string;
  synced_at: string;
  created_at: string;
}

export interface CommissionsFilters {
  marketplace?: OfficialCommissionMarketplace | undefined;
  dateFrom?: string | undefined;
  page: number;
  pageSize: number;
}

export interface CommissionsPage {
  data: Commission[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Fetch: lista paginada
// ---------------------------------------------------------------------------

const COMMISSIONS_COLUMNS =
  "id, tenant_id, marketplace_code, external_transaction_id, order_id, status, product_name, sale_amount_cents, commission_amount_cents, currency, transaction_date, synced_at, created_at";

async function fetchCommissions(filters: CommissionsFilters): Promise<CommissionsPage> {
  const { marketplace, dateFrom, page, pageSize } = filters;

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from("commissions")
    .select(COMMISSIONS_COLUMNS, { count: "exact" })
    .order("transaction_date", { ascending: false })
    .range(from, to);

  if (marketplace) {
    query = query.eq("marketplace_code", marketplace);
  }

  if (dateFrom) {
    query = query.gte("transaction_date", dateFrom);
  }

  const { data, error, count } = await query;

  if (error) throw error;

  const total = count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    data: (data ?? []) as Commission[],
    total,
    page,
    pageSize,
    totalPages,
  };
}

export function useCommissions(filters: CommissionsFilters = { page: 1, pageSize: 20 }) {
  return useQuery<CommissionsPage, Error>({
    queryKey: ["commissions", filters],
    queryFn: () => fetchCommissions(filters),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Resumo por marketplace (para os cards) - agregado client-side, sem view SQL
// nova (mesmo padrao de fetchStatusCounts em useConversions.ts).
// ---------------------------------------------------------------------------

export interface MarketplaceCommissionsSummary {
  marketplaceCode: OfficialCommissionMarketplace;
  count: number;
  totalSaleCents: number;
  totalCommissionCents: number;
}

// PostgREST corta a resposta em max_rows (1000, ver supabase/config.toml) SEM
// erro nem sinalizacao - so devolve as 1000 primeiras linhas em silencio.
// Buscar tudo numa unica query (sem .range()) fazia o card de resumo
// subestimar o total assim que um tenant passasse de 1000 linhas em
// commissions, sem qualquer aviso de truncamento. Busca em blocos explicitos
// ate esgotar os dados, agregando no client sobre TODAS as linhas.
const SUMMARY_PAGE_SIZE = 1000;

async function fetchCommissionsSummary(dateFrom?: string): Promise<MarketplaceCommissionsSummary[]> {
  const byMarketplace = new Map<OfficialCommissionMarketplace, MarketplaceCommissionsSummary>();

  for (let page = 0; ; page++) {
    const from = page * SUMMARY_PAGE_SIZE;
    const to = from + SUMMARY_PAGE_SIZE - 1;

    let query = supabase
      .from("commissions")
      .select("marketplace_code, sale_amount_cents, commission_amount_cents")
      .range(from, to);

    if (dateFrom) {
      query = query.gte("transaction_date", dateFrom);
    }

    const { data, error } = await query;
    if (error) throw error;

    const rows = data ?? [];
    for (const row of rows) {
      const code = row.marketplace_code as OfficialCommissionMarketplace;
      const existing = byMarketplace.get(code) ?? {
        marketplaceCode: code,
        count: 0,
        totalSaleCents: 0,
        totalCommissionCents: 0,
      };
      byMarketplace.set(code, {
        marketplaceCode: code,
        count: existing.count + 1,
        totalSaleCents: existing.totalSaleCents + (row.sale_amount_cents ?? 0),
        totalCommissionCents: existing.totalCommissionCents + (row.commission_amount_cents ?? 0),
      });
    }

    if (rows.length < SUMMARY_PAGE_SIZE) {
      break;
    }
  }

  return Array.from(byMarketplace.values());
}

export function useCommissionsSummary(dateFrom?: string) {
  return useQuery<MarketplaceCommissionsSummary[], Error>({
    queryKey: ["commissions-summary", dateFrom],
    queryFn: () => fetchCommissionsSummary(dateFrom),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
